import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NodeProps, Handle, Position } from 'reactflow';
import dayjs from 'dayjs';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import ConditionalTreeNode from '../../../../types/BotBuilder/ConditionalTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import { Badge } from '../../../../components/ui/badge';
import SlotValueType, {
  SlotValueTypeLabels
} from '../../../../types/BotBuilder/SlotValueType';
import ComparisonOperator from '../../../../types/BotBuilder/ComparisonOperator';
import IntentSlot from '../../../../types/BotBuilder/IntentSlot';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import {
  getConditionalOperatorOptions,
  normaliseConditionalState,
  operatorRequiresComparisonValue
} from './conditionalNodeUtils';

export type ConditionalNodeData = {
  node: BotResponseTreeNode;
  highlightedHandle?: string | null;
  isLocked?: boolean;
};

const isConditionalTreeNode = (
  content: any
): content is ConditionalTreeNode => {
  return content instanceof ConditionalTreeNode;
};

const getVariableLabel = (option: any, intentSlotMap: any): string => {
  if (option.isSystemVariable) {
    return 'System';
  }

  if (intentSlotMap[option.slotName]) {
    return SlotValueTypeLabels[
      intentSlotMap[option.slotName].valueType as SlotValueType
    ];
  }

  if (
    option.valueType &&
    SlotValueTypeLabels[option.valueType as SlotValueType]
  ) {
    return SlotValueTypeLabels[option.valueType as SlotValueType];
  }

  return '';
};

const getSlotValueType = (
  slot: any,
  intentSlotMap: any
): SlotValueType | undefined => {
  if (!slot) return undefined;
  if (slot.isSystemVariable) {
    return slot.valueType;
  }
  return intentSlotMap[slot.slotName]?.valueType ?? slot.valueType;
};

function ConditionalNode(
  props: NodeProps<ConditionalNodeData>
): React.ReactElement {
  const { data, isConnectable } = props;
  const { intentSlotMap, intent, onNodeUpdate, onNodeDelete, systemVariables } =
    useContext(BotBuilderContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const conditionalNode = useMemo(
    () =>
      isConditionalTreeNode(data.node.content)
        ? data.node.content
        : new ConditionalTreeNode(null, null, null, null, null, null, null),
    [data.node.content]
  );

  const [operator, setOperator] = useState<ComparisonOperator>(
    conditionalNode.comparisonOperator || ('' as ComparisonOperator)
  );
  const [comparisonValue, setComparisonValue] = useState<any>(
    conditionalNode.comparisonValue || ''
  );

  const currentSlot = useMemo(() => {
    if (!conditionalNode?.slotName) return null;
    return intentSlotMap[conditionalNode.slotName] || null;
  }, [conditionalNode?.slotName, intentSlotMap]);

  useEffect(() => {
    if (!conditionalNode) return;
    if (
      currentSlot &&
      conditionalNode.intentSlotId !== currentSlot.intentSlotId
    ) {
      conditionalNode.intentSlotId = currentSlot.intentSlotId;
      conditionalNode.slotType = currentSlot.slotName;
      onNodeUpdate(data.node);
    }
  }, [conditionalNode, currentSlot, data.node, onNodeUpdate]);

  const availableVariables = useMemo(() => {
    if (!intent || !intentSlotMap) return [];

    const intentSlots = intent.slots.map((slot: IntentSlot) => ({
      ...slot,
      valueType:
        intentSlotMap[slot.slotName]?.valueType ??
        slot.inlineValueType ??
        SlotValueType.FREE_TEXT,
      isSystemVariable: false
    }));

    const systemVars = systemVariables.map((variable) => ({
      id: null,
      slotName: variable.name,
      slotType: variable.name,
      valueType: variable.valueType,
      isSystemVariable: true,
      description: variable.description
    }));

    return [...intentSlots, ...systemVars];
  }, [intent, intentSlotMap, systemVariables]);

  const selectedIntentSlot = useMemo(() => {
    if (!conditionalNode.slotName) return null;

    if (conditionalNode.slotName.startsWith('chatbotContext.system.')) {
      const systemVarName = conditionalNode.slotName.replace(
        'chatbotContext.system.',
        ''
      );
      return (
        availableVariables.find(
          (variable) =>
            variable.slotName === systemVarName && variable.isSystemVariable
        ) || null
      );
    }

    return (
      availableVariables.find(
        (variable) => variable.slotName === conditionalNode.slotName
      ) || null
    );
  }, [conditionalNode.slotName, availableVariables]);

  const selectedSlotValueType = useMemo(
    () => getSlotValueType(selectedIntentSlot, intentSlotMap),
    [selectedIntentSlot, intentSlotMap]
  );

  const operatorOptions = useMemo(
    () => getConditionalOperatorOptions(selectedSlotValueType),
    [selectedSlotValueType]
  );

  useEffect(() => {
    if (!selectedIntentSlot) {
      return;
    }

    const {
      operator: normalisedOperator,
      comparisonValue: normalisedComparisonValue
    } = normaliseConditionalState(
      selectedSlotValueType,
      operator,
      comparisonValue
    );

    if (operator !== normalisedOperator) {
      setOperator(normalisedOperator);
    }

    if (comparisonValue !== normalisedComparisonValue) {
      setComparisonValue(normalisedComparisonValue);
    }
  }, [comparisonValue, operator, selectedIntentSlot, selectedSlotValueType]);

  useEffect(() => {
    if (!intent || !conditionalNode) return;
    if (!conditionalNode.slotType && conditionalNode.slotName) {
      const intentSlot = intent.slots.find(
        (islot) => islot.slotName === conditionalNode.slotName
      );
      if (
        intentSlot?.slotType &&
        (conditionalNode.slotType !== intentSlot.slotType ||
          conditionalNode.intentSlotId !== intentSlot.id)
      ) {
        conditionalNode.slotType = intentSlot.slotType;
        conditionalNode.intentSlotId = intentSlot.id;
        onNodeUpdate(data.node);
      }
    }
  }, [intent, conditionalNode, data.node, onNodeUpdate]);

  useDebouncedEffect(
    () => {
      const newSlotId = selectedIntentSlot ? selectedIntentSlot.id : null;
      const newSlotName = selectedIntentSlot
        ? selectedIntentSlot.slotName
        : null;
      const newComparisonValue = operatorRequiresComparisonValue(operator)
        ? comparisonValue
        : null;
      if (
        conditionalNode.intentSlotId !== newSlotId ||
        conditionalNode.slotName !== newSlotName ||
        conditionalNode.comparisonOperator !== operator ||
        conditionalNode.comparisonValue !== newComparisonValue
      ) {
        conditionalNode.intentSlotId = newSlotId;
        conditionalNode.slotType = newSlotName;
        conditionalNode.slotName = newSlotName;
        conditionalNode.comparisonOperator = operator;
        conditionalNode.comparisonValue = newComparisonValue;
        onNodeUpdate(data.node);
      }
    },
    [selectedIntentSlot, operator, comparisonValue],
    300
  );

  const renderOutcomeBox = (
    label: string,
    handleId: string
  ): React.ReactElement => {
    const isHighlighted = data.highlightedHandle === handleId;
    const highlightClass = isHighlighted
      ? 'ring-2 ring-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
      : '';

    return (
      <div className='mb-2 relative w-full'>
        <CustomInput
          name={`outcome-${handleId}`}
          value={label}
          onChange={() => {}}
          fullWidth
          className={`bg-white ${highlightClass}`}
          containerClassName='mb-0'
          disabled
        />
        <Handle
          type='source'
          position={Position.Right}
          id={handleId}
          style={{
            position: 'absolute',
            right: -26,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1
          }}
          isConnectable={isConnectable}
        />
      </div>
    );
  };

  return (
    <BotBuilderNode
      title='Condition'
      sourceNode={data.node}
      onNodeDelete={async () => {
        onNodeDelete(data.node);
      }}
      deleteButtonRef={deleteButtonRef}
      showAddButton={false}
      isLocked={data.isLocked}
    >
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -10 }}
        isConnectable={isConnectable}
        id='conditional-input'
      />
      <div className='flex flex-col gap-2'>
        <CustomSelect
          label='Select variable'
          value={selectedIntentSlot ? selectedIntentSlot.slotName : ''}
          onValueChange={(value) => {
            const selectedVar = availableVariables.find(
              (v) => v.slotName === value
            );
            let nextSlotName = null;
            if (selectedVar) {
              nextSlotName = selectedVar.isSystemVariable
                ? `chatbotContext.system.${selectedVar.slotName}`
                : selectedVar.slotName;
            }
            const {
              operator: normalisedOperator,
              comparisonValue: normalisedComparisonValue
            } = normaliseConditionalState(
              getSlotValueType(selectedVar, intentSlotMap),
              operator,
              comparisonValue
            );

            if (selectedVar) {
              conditionalNode.intentSlotId = selectedVar.id;
              conditionalNode.slotType = selectedVar.slotName;
            } else {
              conditionalNode.intentSlotId = null;
              conditionalNode.slotType = null;
            }
            conditionalNode.slotName = nextSlotName;
            conditionalNode.comparisonOperator = normalisedOperator;
            conditionalNode.comparisonValue = operatorRequiresComparisonValue(
              normalisedOperator
            )
              ? normalisedComparisonValue
              : null;
            setOperator(normalisedOperator);
            setComparisonValue(normalisedComparisonValue);
            onNodeUpdate(data.node);
          }}
          placeholder='Select a variable'
          options={availableVariables.map((option) => ({
            value: option.slotName,
            label: (
              <div className='flex items-center justify-between w-full'>
                <span className='flex-1'>{option.slotName}</span>
                <Badge
                  variant='outline'
                  className={`text-xs ml-2 ${
                    option.isSystemVariable
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-teal-600 text-white border-teal-600'
                  }`}
                >
                  {getVariableLabel(option, intentSlotMap)}
                </Badge>
              </div>
            )
          }))}
          className='bg-white'
          disabled={data.isLocked}
        />
        <CustomSelect
          label='Condition'
          value={operator || ''}
          onValueChange={(value) => {
            const newOperator = value as ComparisonOperator;
            setOperator(newOperator);
            conditionalNode.comparisonOperator = newOperator;
            if (!operatorRequiresComparisonValue(newOperator)) {
              conditionalNode.comparisonValue = null;
              setComparisonValue('');
            }
            onNodeUpdate(data.node);
          }}
          placeholder='Select condition'
          options={operatorOptions}
          className='bg-white'
          disabled={data.isLocked}
        />
        {operator &&
          operatorRequiresComparisonValue(operator) &&
          ((): React.ReactElement => {
            const isDateField = selectedSlotValueType === SlotValueType.DATE;

            if (isDateField) {
              return (
                <CustomInput
                  name='comparison-date'
                  label='Comparison date'
                  type='date'
                  value={
                    comparisonValue
                      ? dayjs(comparisonValue, 'DD-MM-YYYY').format(
                          'YYYY-MM-DD'
                        )
                      : ''
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setComparisonValue(
                      e.target.value
                        ? dayjs(e.target.value).format('DD-MM-YYYY')
                        : ''
                    );
                  }}
                  fullWidth
                  className='bg-white'
                  containerClassName='mb-4'
                  disabled={data.isLocked}
                />
              );
            }

            return (
              <CustomInput
                name='comparison-value'
                label='Comparison value'
                type={
                  selectedSlotValueType === SlotValueType.NUMBER
                    ? 'number'
                    : 'text'
                }
                value={comparisonValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setComparisonValue(e.target.value)
                }
                fullWidth
                className='bg-white'
                containerClassName='mb-4'
                disabled={data.isLocked}
              />
            );
          })()}
      </div>
      <div className='mt-2'>
        {renderOutcomeBox('True', `${data.node.id}-true-handle`)}
        {renderOutcomeBox('False', `${data.node.id}-false-handle`)}
      </div>
    </BotBuilderNode>
  );
}

export default ConditionalNode;
